import React, { useMemo, useState } from 'react';
import CategoryFilterBar from './CategoryFilterBar';
import InlineLoading from './InlineLoading';
import ProductSearchInput from './ProductSearchInput';
import useProductSearchSettings from '../hooks/useProductSearchSettings';
import {
  fieldsMatchProductSearchTerms,
  tokenizeProductSearchQuery,
} from '../utils/productSearch';

const PosSlotPickerModal = ({
  isOpen,
  onClose,
  entries = [],
  onSelect,
  categories = [],
  selectedCategory = 'all',
  onCategorySelect,
  categoriesLoading = false,
  categoriesError = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { searchMode } = useProductSearchSettings();
  const suggestionProducts = useMemo(() => {
    const byId = new Map();
    (entries || []).forEach((entry) => {
      if (!entry?.product) return;
      byId.set(entry.product.id ?? entry.product.productName, entry.product);
    });
    return Array.from(byId.values());
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const searchTokens = tokenizeProductSearchQuery(searchTerm);
    return (entries || []).filter((entry) => {
      const matchesCategory = selectedCategory === 'all'
        || String(entry?.categoryId) === String(selectedCategory);
      if (!matchesCategory) return false;
      if (!searchTokens.length) return true;
      return fieldsMatchProductSearchTerms([
        entry?.productName,
        entry?.varietyName,
        entry?.metricUnit,
        entry?.description,
        entry?.price?.barcode,
      ], searchTokens, { searchMode });
    });
  }, [entries, searchTerm, selectedCategory, searchMode]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/50 px-4 py-6"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="w-full max-w-3xl rounded-2xl border border-gray-100 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-100 bg-gray-50 px-6 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">POS favs</div>
            <h2 className="mt-1 text-lg font-semibold text-gray-900">Agregar precio favorito</h2>
            <p className="mt-1 text-xs text-gray-500">
              Selecciona una variedad y su precio exacto para agregarlo a Favs.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4 p-6">
          <CategoryFilterBar
            categories={categories}
            selectedCategory={selectedCategory}
            onSelect={onCategorySelect}
            includeAll
            allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
            loading={categoriesLoading}
            loadingMessage={<InlineLoading label="Cargando categorias" />}
            error={categoriesError}
            emptyMessage="No hay categorias disponibles para esta caja."
            isEmpty={categories.length === 0}
          />

          <ProductSearchInput
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar producto, variedad o precio..."
            products={suggestionProducts}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500"
          />

          <div className="max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {filteredEntries.length ? filteredEntries.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  onClick={() => {
                    onSelect?.(entry);
                    setSearchTerm('');
                  }}
                  className={`flex min-h-[176px] flex-col items-center justify-center overflow-hidden rounded-2xl border p-3 text-center shadow-sm transition ${
                    entry.soldOut
                      ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 opacity-80 grayscale'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                  }`}
                  disabled={entry.soldOut}
                  title={`${entry.productName} · ${entry.varietyName} · ${entry.metricUnit}`}
                >
                  <img
                    src={entry.imageSrc || 'https://placehold.co/200'}
                    alt=""
                    className="h-14 w-14 rounded-xl object-cover shadow-sm"
                  />
                  <div
                    className="mt-3 w-full overflow-hidden px-1 text-center text-xs font-semibold leading-tight text-gray-900"
                    style={{
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                    }}
                  >
                    {entry.varietyName}
                  </div>
                  <div className="mt-1 w-full truncate text-[10px] font-medium text-gray-500">
                    {entry.productName}
                  </div>
                  <div className="mt-1 flex w-full items-center justify-center gap-1 text-[10px] font-bold text-blue-700">
                    <span className="truncate">{entry.metricUnit}</span>
                    <span aria-hidden="true">·</span>
                    <span className="shrink-0">{entry.priceLabel}</span>
                  </div>
                </button>
              )) : (
                <div className="col-span-full rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500">
                  No hay precios que coincidan con la búsqueda.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosSlotPickerModal;
